// stores/user.ts
// Correction vs version Vue3 :
//   • userId était absent du `return` → isLoggedIn basé sur userId (plus fiable)
//     car le token MSAL est null au retour d'un compte existant sans re-login.
//   • On accepte userId comme clé de présence de session (MSAL set active account).

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { UserProfileDto } from '~/types/AccountProfile'
import type { ProfileShareable } from '~/types/NotificationsHub'
import { useProfileVisibilityStore } from '~/stores/profileVisibilityStore'

interface UserLoginPayload {
  userId: string
  firstName: string
  avatar?: string | null
  mbti?: string | null
  profileCompletion?: number
  token?: string | null
}

export interface UserProfileUpdate {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  phone?: string | null
  mbti?: string
  profileCompletion?: number
  avatar?: string
}

export const useUserStore = defineStore('user', () => {
  // ── State ──────────────────────────────────────────────────────────────────
  const userId: Ref<string | null> = ref(null)
  const firstName: Ref<string | null> = ref(null)
  const lastName: Ref<string | null> = ref(null)
  const email: Ref<string | null> = ref(null)
  const phone: Ref<string | null> = ref(null)
  const shareId: Ref<string | null> = ref(null)
  const isShareable: Ref<boolean> = ref(false)
  const avatar: Ref<string | null> = ref(null)
  const mbti: Ref<string | null> = ref(null)
  const profileCompletion: Ref<number> = ref(0)
  const token: Ref<string | null> = ref(null) // accessToken MSAL (volatile)

  // ── Getters ────────────────────────────────────────────────────────────────
  // On utilise userId plutôt que token : un compte déjà connecté (comptes existants
  // en cache MSAL) ne repasse pas par loginRedirect → token reste null en store
  // mais userId est bien rempli via handleRedirectCallback.
  const isLoggedIn: ComputedRef<boolean> = computed((): boolean => !!userId.value)

  /** `true` si un numéro de téléphone non vide est renseigné dans le profil. */
  const hasPhone: ComputedRef<boolean> = computed((): boolean => {
    return (phone.value?.trim() ?? '') !== ''
  })

  // ── Actions ────────────────────────────────────────────────────────────────
  /**
   * Stocke les informations utilisateur en session côté front.
   * @param {UserLoginPayload} payload Données de session utilisateur.
   */
  function login(payload: UserLoginPayload) {
    userId.value = payload.userId
    firstName.value = payload.firstName
    avatar.value = payload.avatar ?? null
    mbti.value = payload.mbti ?? null
    profileCompletion.value = payload.profileCompletion ?? 0
    token.value = payload.token ?? null

    console.log('User logged in:', { payload: payload })
  }

  /**
   * Réinitialise la session utilisateur en mémoire.
   */
  function logout() {
    userId.value = null
    firstName.value = null
    lastName.value = null
    email.value = null
    phone.value = null
    shareId.value = null
    isShareable.value = false
    avatar.value = null
    mbti.value = null
    profileCompletion.value = 0
    token.value = null
  }

  /**
   * Met à jour des champs de profil partiels (sans écraser les absents).
   * @param {UserProfileUpdate} data Données partielles de profil.
   */
  function updateProfile(data: UserProfileUpdate) {
    if (data.firstName !== undefined) firstName.value = data.firstName
    if (data.lastName !== undefined) lastName.value = data.lastName
    if (data.email !== undefined) email.value = data.email
    if (data.phone !== undefined) phone.value = data.phone
    if (data.mbti !== undefined) mbti.value = data.mbti
    if (data.profileCompletion !== undefined) profileCompletion.value = data.profileCompletion
    if (data.avatar !== undefined) avatar.value = data.avatar
  }

  /**
   * Hydrate le profil depuis `GET /api/Profile/me` (champs non vides uniquement).
   * @param {UserProfileDto} data - Corps JSON de l'API.
   * @param {string | null} fallbackEmail - E-mail MSAL si l'API ne le renvoie pas.
   */
  function setProfilFromMe(data: UserProfileDto, fallbackEmail: string | null): void {
    const profilFieldsToUpdate: UserProfileUpdate = {}
    const apiFirstName: string = data.firstName?.trim() ?? ''
    const apiLastName: string = data.lastName?.trim() ?? ''
    const apiPhone: string = data.phone?.trim() ?? ''
    const resolvedEmail: string = data.email?.trim() || fallbackEmail || ''

    if (apiFirstName !== '') profilFieldsToUpdate.firstName = apiFirstName
    if (apiLastName !== '') profilFieldsToUpdate.lastName = apiLastName
    if (apiPhone !== '') profilFieldsToUpdate.phone = apiPhone
    if (resolvedEmail !== '') profilFieldsToUpdate.email = resolvedEmail

    if (Object.keys(profilFieldsToUpdate).length > 0) updateProfile(profilFieldsToUpdate)

    if (data.shareId) shareId.value = data.shareId

    if (data.isShareable) {
      isShareable.value = true
    }

    if (data.consentObj) {
      useProfileVisibilityStore().setConsentObj(data.consentObj)
    }
  }

  /**
   * Met à jour uniquement le jeton d'accès API (après `acquireTokenSilent`, etc.).
   * @param {string | null} accessToken Nouveau jeton ou `null`.
   */
  function setAccessToken(accessToken: string | null) {
    token.value = accessToken
  }

  /**
   * Profil devenu partageable (SignalR `ProfileShareableChanged`).
   * @param {ProfileShareable} payload - Indicateur poussé par le hub.
   * @returns {void}
   */
  function setShareable(payload: ProfileShareable): void {
    isShareable.value = payload.isShareable
  }

  return {
    // state
    userId,
    firstName,
    lastName,
    email,
    phone,
    shareId,
    isShareable,
    avatar,
    mbti,
    profileCompletion,
    token,
    // getters
    isLoggedIn,
    hasPhone,
    // actions
    login,
    logout,
    updateProfile,
    setProfilFromMe,
    setAccessToken,
    setShareable,
  }
})
